import { useMemo } from "react"
import { readInlineData } from "react-relay"
import { useOutletContext } from "react-router"
import { startOfDay } from "date-fns"
import { orderBy } from "es-toolkit/array"
import { DATE_FORMATS, parseDateFromString } from "@/lib/format-date"
import { chMedicationFragmentInline } from "@/modules/patients/detail/medication/ch/services/chMedicationFragment"
import { deMedicationFragmentInline } from "@/modules/patients/detail/medication/de/services/deMedicationFragment"
import {
  MedicationFragmentInline$data,
  MedicationFragmentInline$key,
} from "@/modules/patients/detail/medication/types/medication-fragment-type"
import { MedicationData, MedicationListType } from "@/modules/patients/detail/medication/types/medication-type"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { Country } from "@/types/__generated__/union-types"
import { ArrElement } from "@/types/ArrElement"

const getMedicationFragmentInline = (country: Country) => {
  switch (country) {
    case "DE":
      return deMedicationFragmentInline
    case "CH":
      return chMedicationFragmentInline
  }
}

const generateActiveAndInactiveMedicationList = <T extends MedicationFragmentInline$data[]>(
  medications: MedicationListType,
  country: Country,
) => {
  const today = startOfDay(new Date())

  // a condition to identify active medications
  const isActive = (medication: ArrElement<MedicationListType>) => {
    const startDate = medication.node.startDate
      ? parseDateFromString(medication.node.startDate, DATE_FORMATS.ISODateFormat)
      : null
    const endDate = medication.node.endDate
      ? parseDateFromString(medication.node.endDate, DATE_FORMATS.ISODateFormat)
      : null

    const isAfterStartDate = startDate ? today >= startDate : true
    const isBeforeEndDate = endDate ? today <= endDate : true

    return isAfterStartDate && isBeforeEndDate
  }

  const activeList = medications.filter(isActive)
  const inactiveList = medications.filter((medication) => !isActive(medication))

  const medicationFragmentInline = getMedicationFragmentInline(country)

  // using readInlineData to retrieve active/inactive medication fragment data

  const activeMedications = <T>orderBy(
    activeList.map((medication) =>
      readInlineData<MedicationFragmentInline$key>(medicationFragmentInline, medication.node),
    ),
    [(item) => !item.startDate, (item) => item.startDate],
    ["desc", "asc"],
  )

  const inactiveMedications = <T>orderBy(
    inactiveList.map((medication) =>
      readInlineData<MedicationFragmentInline$key>(medicationFragmentInline, medication.node),
    ),
    [(item) => item.endDate],
    ["desc"],
  )

  return { activeMedications, inactiveMedications }
}

const useMedications = <T extends MedicationFragmentInline$data[]>() => {
  const countryCode = useCountryParam()
  const { medications } = useOutletContext<MedicationData>()

  const medicationsMemo = useMemo(
    () =>
      medications?.edges?.length
        ? generateActiveAndInactiveMedicationList<T>(medications.edges, countryCode)
        : { activeMedications: [], inactiveMedications: [] },
    [medications, countryCode],
  )

  return medicationsMemo
}

export { useMedications, generateActiveAndInactiveMedicationList }
